import React from 'react';
import { fileUrl } from '../lib/api';
import { peso, manilaDate } from '../lib/format';
import { PlateChip } from './PlateChip';

// Shared billing statement document (used internally and on the public page).
// snapshot = billing_statements.snapshot_json
export const BillingDocument = ({ bsNumber, snapshot, createdAt, paid, orNumber }) => {
  const s = snapshot;
  return (
    <div className="print-surface relative mx-auto max-w-[800px] bg-white rounded-2xl border border-black/5 shadow-[var(--shadow-soft)] p-5 sm:p-8 text-slate-900">
      {paid && (
        <div className="pointer-events-none select-none absolute inset-0 flex items-center justify-center text-6xl font-semibold tracking-widest text-emerald-600/10 rotate-[-18deg]">
          PAID
        </div>
      )}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          {s.shop.logo_url && (
            <img src={fileUrl(s.shop.logo_url)} alt="logo" className="h-14 w-14 rounded-xl object-cover border border-black/5" />
          )}
          <div>
            <h1 className="text-lg font-semibold font-display">{s.shop.name}</h1>
            <p className="text-sm text-slate-600">{s.shop.address}</p>
            <p className="text-sm text-slate-600">
              {s.shop.mobile}{s.shop.tin ? ` · TIN: ${s.shop.tin}` : ''}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wide text-slate-500">Billing statement</p>
          <p data-testid="billing-bs-number" className="font-plate font-semibold">{bsNumber}</p>
          <p className="text-xs text-slate-500">{manilaDate(createdAt)}</p>
          {paid && (
            <span data-testid="billing-paid-badge" className="mt-1 inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 text-xs font-medium">
              PAID{orNumber ? ` — OR No. ${orNumber}` : ''}
            </span>
          )}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div className="rounded-xl bg-slate-50 p-3">
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Billed to</p>
          <p className="font-medium">{s.customer.name}</p>
          <p className="text-slate-600">{s.customer.mobile}</p>
        </div>
        <div className="rounded-xl bg-slate-50 p-3">
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Vehicle · {s.jo_number}</p>
          <p className="font-medium">{s.vehicle?.year} {s.vehicle?.make} {s.vehicle?.model}</p>
          {s.vehicle?.plate && <PlateChip plate={s.vehicle.plate} />}
        </div>
      </div>

      <table className="mt-5 w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
            <th className="py-2 pr-2">Description</th>
            <th className="py-2 pr-2 w-14">Qty</th>
            <th className="py-2 pr-2 text-right">Unit price</th>
            <th className="py-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {s.line_items.map((it) => (
            <tr key={it.id} className="border-b border-slate-100">
              <td className="py-2 pr-2">
                <span className={`mr-1.5 text-[10px] uppercase font-medium rounded px-1 py-0.5 ${it.type === 'labor' ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-800'}`}>{it.type}</span>
                {it.description}
              </td>
              <td className="py-2 pr-2 tabular-nums">{it.qty}</td>
              <td className="py-2 pr-2 text-right tabular-nums">{peso(it.unit_price_centavos)}</td>
              <td className="py-2 text-right tabular-nums">{peso(it.amount_centavos)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-4 ml-auto w-full sm:w-[320px] rounded-xl border border-black/10 p-4 flex items-center justify-between">
        <span className="text-sm font-medium uppercase tracking-wide">Total amount due</span>
        <span data-testid="billing-total" className="text-lg font-semibold tabular-nums">{peso(s.total_centavos)}</span>
      </div>

      <p className="mt-6 text-xs text-slate-500 border-t border-slate-100 pt-3">{s.disclaimer}</p>
    </div>
  );
};
